import {
  AfterContentInit,
  Directive,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  QueryList
} from "@angular/core";

import { Observable, merge as observableMerge, of as observableOf } from "rxjs";
import {
  debounceTime,
  filter,
  first,
  map,
  startWith,
  takeUntil
} from "rxjs/operators";

import { ControlValueAccessor } from "@angular/forms";
import { ButtonItemInterface } from "./button-item-interface";

@Directive()
export abstract class AbstractButtonGroup<T extends ButtonItemInterface>
  implements AfterContentInit, OnDestroy, ControlValueAccessor {
  abstract childControls: QueryList<T>;

  @Input()
  get value(): any {
    return this._value;
  }
  set value(value: any) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.valueChange.emit(value);
  }
  private _value: any;

  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  @HostBinding("class.touched")
  get touched(): boolean {
    return this._touched;
  }
  set touched(value: boolean) {
    value = value != null && value !== false;
    if (this._touched === value) {
      return;
    }

    this._touched = value;
    this.touchedChange.emit(value);
  }
  private _touched: boolean = false;

  @Output() touchedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    value = value != null && value !== false;
    if (this._disabled === value) {
      return;
    }

    this._disabled = value;
  }
  private _disabled: boolean = false;
  // note: don't output disable status, only input

  constructor() {
    this.initControlAccessors();
  }

  ngAfterContentInit(): void {
    this.initValueChangeSubscription();
    this.initChildControlChangeSubscription();
  }

  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();

    // separate takeUntil emitter for canceling childControl
    // subscription whenever childControls change.
    this.cancelChildControlSubscription.complete();
  }
  private onDestroyEmitter = new EventEmitter<void>();

  /**
   * In response to childControls changing, the group needs to:
   * - Set selected true/false on any new buttons
   * - Re-subscribe to child control changes
   * @private
   */
  private initChildControlChangeSubscription(): void {
    this.childControls.changes
      .pipe(
        startWith(true), // startWith(true) to run subscription once immediately
        debounceTime(0), // debounce by 1 frame to avoid changeAfterChecked errors
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe(() => {
        this.updateChildControls();
        this.listenChildControlChanges();
      });
  }

  private initValueChangeSubscription(): void {
    observableMerge(
      this.valueChange,

      // Delay initial update one frame.
      // Otherwise changes to child control state will trigger changeAfterChecked errors.
      observableOf(true).pipe(debounceTime(0))
    )
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.updateChildControls());
  }

  private listenChildControlChanges(): void {
    this.cancelChildControlSubscription.emit();
    let childControlChange$: Observable<any> = observableMerge(
      ...this.childControls.toArray().map((childControl) =>
        childControl.checkedChange.pipe(
          filter((checked) => checked),
          map(() => childControl.value)
        )
      )
    );

    const cancelEmitter = observableMerge(
      this.cancelChildControlSubscription,
      this.onDestroyEmitter
    );
    childControlChange$
      .pipe(takeUntil(cancelEmitter))
      .subscribe((radioValue) => {
        this.value = radioValue;
      });

    if (!this.touched) {
      this.handleFirstTouch();
    }
  }
  private cancelChildControlSubscription = new EventEmitter<void>();

  private handleFirstTouch(): void {
    let childControlTouch$: Observable<boolean> = observableMerge(
      ...this.childControls
        .toArray()
        .map((childControl) => childControl.touchedChange)
    );

    childControlTouch$
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        this.touched = true;
      });
  }

  private updateChildControls(): void {
    this.childControls.toArray().forEach((childControl) => {
      childControl.checked = this.value === childControl.value;
    });
  }

  private initControlAccessors(): void {
    this.valueChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((value) => {
        if (!this._onChangeFn) {
          return;
        }
        this._onChangeFn(value);
      });

    this.touchedChange
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        if (this._onTouchedFn != null) {
          this._onTouchedFn();
        }
      });
  }

  writeValue(value: any): void {
    this.value = value;
  }

  private _onChangeFn?: (value: boolean) => void;
  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  private _onTouchedFn?: () => void;
  registerOnTouched(fn: any): void {
    this._onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
